import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import adminApiService from '../../services/adminApiService';
import { toast } from 'react-toastify';

const AdminSchedulers = () => {
  const navigate = useNavigate();
  const [schedulers, setSchedulers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runningId, setRunningId] = useState(null);
  const [editingScheduler, setEditingScheduler] = useState(null);
  const [newCron, setNewCron] = useState('');
  const [savingCron, setSavingCron] = useState(false);

  useEffect(() => {
    fetchSchedulers();
  }, []);

  const fetchSchedulers = async () => {
    try {
      setLoading(true);
      const response = await adminApiService.getSchedulers();
      if (response.success) {
        setSchedulers(response.schedulers || []);
      } else {
        toast.error(response.message || 'Failed to fetch schedulers');
      }
    } catch (error) {
      console.error('Error fetching schedulers:', error);
      toast.error('Failed to load schedulers');
    } finally {
      setLoading(false);
    }
  };

  const handleRunImmediately = async (service, id) => {
    try {
      setRunningId(id);
      toast.info(`Triggering scheduler '${id}'...`);
      const response = await adminApiService.runScheduler(service, id);
      if (response.success) {
        toast.success(response.message || 'Scheduler run triggered successfully in the background!');
        // Refresh list to show updated status after a short delay
        setTimeout(fetchSchedulers, 2000);
      } else {
        toast.error(response.message || 'Failed to trigger scheduler');
      }
    } catch (error) {
      console.error('Error running scheduler:', error);
      toast.error('Failed to execute scheduler run');
    } finally {
      setRunningId(null);
    }
  };

  const startEditCron = (scheduler) => {
    setEditingScheduler(scheduler);
    setNewCron(scheduler.cron);
  };

  const cancelEditCron = () => {
    setEditingScheduler(null);
    setNewCron('');
  };

  const handleSaveCron = async (e) => {
    e.preventDefault();
    if (!newCron.trim()) {
      toast.error('Cron expression cannot be empty');
      return;
    }

    try {
      setSavingCron(true);
      const response = await adminApiService.updateSchedulerCron(
        editingScheduler.service,
        editingScheduler.id,
        newCron
      );

      if (response.success) {
        toast.success('Scheduler time updated successfully!');
        setEditingScheduler(null);
        fetchSchedulers();
      } else {
        toast.error(response.message || 'Failed to update scheduler cron');
      }
    } catch (error) {
      console.error('Error updating cron:', error);
      toast.error('An error occurred. Check cron format (must be a valid Spring 6-field Cron Trigger).');
    } finally {
      setSavingCron(false);
    }
  };

  const formatDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return 'Never';
    try {
      const date = new Date(dateTimeStr);
      return date.toLocaleString();
    } catch (e) {
      return dateTimeStr;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          <p className="mt-4 text-gray-500 font-medium">Loading Schedulers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div className="flex items-center">
          <button 
            onClick={() => navigate('/admin/dashboard/settings')}
            className="mr-4 p-2 text-gray-500 hover:text-gray-700 bg-white shadow-sm border border-gray-200 rounded-full hover:bg-gray-100 transition-all duration-200"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <div>
            <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">System Schedulers</h1>
            <p className="text-gray-600 mt-1">Configure intervals and trigger background processes across applications</p>
          </div>
        </div>
        <button 
          onClick={fetchSchedulers}
          className="flex items-center space-x-2 py-2 px-4 bg-white border border-gray-300 rounded-lg shadow-sm text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 1121.21 8H18.5" />
          </svg>
          <span>Refresh</span>
        </button>
      </div>

      {/* Schedulers Grid */}
      <div className="grid grid-cols-1 gap-6">
        {schedulers.map((scheduler) => (
          <div 
            key={scheduler.id} 
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200"
          >
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 pb-4 border-b border-gray-100 gap-4">
              <div>
                <div className="flex items-center space-x-3 flex-wrap gap-y-2">
                  <h3 className="text-xl font-bold text-gray-900">{scheduler.name}</h3>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                    scheduler.service === 'hotelapp' 
                      ? 'bg-indigo-50 text-indigo-700 border border-indigo-100' 
                      : 'bg-teal-50 text-teal-700 border border-teal-100'
                  }`}>
                    {scheduler.service === 'hotelapp' ? 'hotelapp (Frontend)' : 'hotel-api (Backend)'}
                  </span>
                </div>
                <p className="text-gray-500 text-xs mt-1 font-mono bg-gray-50 px-2 py-1 rounded inline-block">
                  Key: {scheduler.id}
                </p>
              </div>

              <div className="flex items-center space-x-3 w-full md:w-auto">
                <button
                  onClick={() => startEditCron(scheduler)}
                  className="flex-1 md:flex-none inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50 shadow-sm transition-colors duration-150"
                >
                  Edit Time
                </button>
                <button
                  onClick={() => handleRunImmediately(scheduler.service, scheduler.id)}
                  disabled={runningId !== null}
                  className="flex-1 md:flex-none inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-lg text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
                >
                  {runningId === scheduler.id ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                      </svg>
                      Triggering...
                    </span>
                  ) : (
                    'Run Immediately'
                  )}
                </button>
              </div>
            </div>

            {/* Description & Config */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 text-sm">
              <div className="lg:col-span-2">
                <h4 className="font-semibold text-gray-700 mb-1">Description</h4>
                <p className="text-gray-600 leading-relaxed">{scheduler.description}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-100 space-y-3">
                <div>
                  <span className="text-gray-500 block text-xs uppercase font-bold tracking-wider">Cron Schedule</span>
                  <span className="font-mono text-gray-900 font-semibold">{scheduler.cron}</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <span className="text-gray-500 block text-xs uppercase font-bold tracking-wider">Last Run Status</span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${
                      scheduler.lastRunStatus?.startsWith('SUCCESS') 
                        ? 'bg-green-100 text-green-800' 
                        : scheduler.lastRunStatus ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {scheduler.lastRunStatus || 'No log'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500 block text-xs uppercase font-bold tracking-wider">Last Run Time</span>
                    <span className="text-gray-900 font-medium block truncate" title={formatDateTime(scheduler.lastRunTime)}>
                      {formatDateTime(scheduler.lastRunTime)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Edit Cron Modal */}
      {editingScheduler && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex justify-center items-center p-4 z-50 animate-fade-in">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 bg-gray-900 text-white flex justify-between items-center">
              <h3 className="text-lg font-bold">Reschedule: {editingScheduler.name}</h3>
              <button 
                onClick={cancelEditCron}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSaveCron} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Cron Expression (6-field format)
                </label>
                <input
                  type="text"
                  value={newCron}
                  onChange={(e) => setNewCron(e.target.value)}
                  className="w-full font-mono px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 shadow-sm text-gray-900"
                  placeholder="e.g. 0 0 6 * * ?"
                  required
                />
              </div>

              {/* Cron Quick Help */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-xs text-blue-800 space-y-2">
                <p className="font-bold">Spring Cron Syntax Helper:</p>
                <p className="font-mono">Seconds  Minutes  Hours  Day-of-Month  Month  Day-of-Week</p>
                <ul className="list-disc pl-4 space-y-1">
                  <li><span className="font-mono">0 0 * * * *</span> - Run at the start of every hour</li>
                  <li><span className="font-mono">0 0 6 * * ?</span> - Run every day at 06:00 AM</li>
                  <li><span className="font-mono">0 */5 * * * ?</span> - Run every 5 minutes</li>
                  <li>Use <span className="font-mono">?</span> for Day-of-Week if Day-of-Month is specified (and vice-versa).</li>
                </ul>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={cancelEditCron}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingCron}
                  className="px-4 py-2 border border-transparent rounded-lg text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm disabled:opacity-50 transition-colors"
                >
                  {savingCron ? 'Saving...' : 'Update Schedule'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSchedulers;
